/**
 * FlowTime — форматирование времени и подсчёт статистики.
 *
 * Вынесено из компонентов: одни и те же величины показываются в таймере,
 * журнале и отчётах, и расхождение в округлении между ними выглядело бы
 * как ошибка в подсчётах.
 *
 * Названия месяцев, дней недели и единиц времени берутся из `Intl`, а не
 * из зашитых списков: сайт работает на четырёх языках, и подписи графиков
 * должны совпадать с языком страницы. Функции принимают локаль явным
 * параметром, потому что вызываются и из компонентов, и из чистых
 * расчётов, где хуков нет.
 */

import type { FlowEntry } from './flowtime-storage';

export const MS_DAY = 86_400_000;

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Приводит код языка страницы к тегу, понятному `Intl`.
 *
 * Сайт переведён на сербскую латиницу, а `Intl` по коду `sr` отдаёт
 * кириллицу: рядом с кнопкой «Fokus» появлялось «2 сата», в журнале
 * «Данас», на оси графика «сеп». Две письменности в одном интерфейсе
 * читаются как сбой кодировки, поэтому письменность указывается явно.
 */
export function intlLocale(locale: string): string {
  return locale === 'sr' ? 'sr-Latn' : locale;
}

/* -------------------------------------------------------------------------
   Названия месяцев и дней недели
   ------------------------------------------------------------------------- */

/**
 * Форматтеры кэшируются: `Intl.DateTimeFormat` заметно дорог в создании,
 * а карта активности за год строит 371 подпись за одну отрисовку.
 */
const cache = new Map<string, Intl.DateTimeFormat>();

function fmt(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(intlLocale(locale), options);
    cache.set(key, f);
  }
  return f;
}

/** Короткие названия месяцев: «янв», «Jan», «ene». */
export function monthsShort(locale: string): string[] {
  const f = fmt(locale, { month: 'short' });
  return Array.from({ length: 12 }, (_, m) => f.format(new Date(2021, m, 1)));
}

/** Короткие дни недели, начиная с воскресенья: индекс совпадает с `getDay`. */
export function weekdaysShort(locale: string): string[] {
  const f = fmt(locale, { weekday: 'short' });
  // 2021-08-01 — воскресенье, дальше по порядку.
  return Array.from({ length: 7 }, (_, d) => f.format(new Date(2021, 7, 1 + d)));
}

/** Полные дни недели для подсказок на графиках. */
export function weekdaysLong(locale: string): string[] {
  const f = fmt(locale, { weekday: 'long' });
  return Array.from({ length: 7 }, (_, d) => f.format(new Date(2021, 7, 1 + d)));
}

/* -------------------------------------------------------------------------
   Длительности
   ------------------------------------------------------------------------- */

/** 1500 → «25:00», 3725 → «1:02:05». Для крупных цифр таймера. */
export function fmtClock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  return h > 0 ? `${String(h)}:${pad2(m)}:${pad2(rest)}` : `${pad2(m)}:${pad2(rest)}`;
}

const unitCache = new Map<string, Intl.NumberFormat>();

function unit(locale: string, u: 'hour' | 'minute' | 'second'): Intl.NumberFormat {
  const key = `${locale}|${u}`;
  let f = unitCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(intlLocale(locale), { style: 'unit', unit: u, unitDisplay: 'short' });
    unitCache.set(key, f);
  }
  return f;
}

/**
 * 3900 → «1 ч 5 мин», «1 hr 5 min». Для итогов, где секунды не важны.
 *
 * Единицы даёт `Intl`: в сербском это «сата», в испанском «h», и зашитое
 * «ч» смотрелось бы там опечаткой.
 */
export function fmtDur(sec: number, locale = 'ru'): string {
  const s = Math.max(0, Math.round(sec));
  if (s === 0) return unit(locale, 'minute').format(0);
  if (s < 60) return unit(locale, 'second').format(s);

  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return unit(locale, 'minute').format(m);
  if (m === 0) return unit(locale, 'hour').format(h);
  return `${unit(locale, 'hour').format(h)} ${unit(locale, 'minute').format(m)}`;
}

/**
 * Компактная длительность для осей графиков: «5 ч» или «30 мин».
 *
 * В отличие от `fmtDur`, показывает одну единицу. На оси Y места мало,
 * и полная запись «2 сата 30 мин» не помещалась в отведённые пиксели:
 * подпись обрезалась слева и превращалась в бессмысленное «30 мин».
 */
export function fmtDurAxis(sec: number, locale = 'ru'): string {
  const s = Math.max(0, Math.round(sec));
  if (s >= 3600) return unit(locale, 'hour').format(Math.round((s / 3600) * 10) / 10);
  return unit(locale, 'minute').format(Math.round(s / 60));
}

export function fmtTime(ts: number): string {
  const d = new Date(ts);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/* -------------------------------------------------------------------------
   Дни
   ------------------------------------------------------------------------- */

/** Ключ дня по местному времени: «2026-09-06». */
export function dayKey(ts: number = Date.now()): string {
  const d = new Date(ts);
  return `${String(d.getFullYear())}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function keyToDate(key: string): Date {
  const [y = 1970, m = 1, d = 1] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function startOfDay(ts: number = Date.now()): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function addDays(ts: number, n: number): number {
  const d = new Date(ts);
  d.setDate(d.getDate() + n);
  return d.getTime();
}

/**
 * «Сегодня», «Вчера» или дата: «6 сент.», «Sep 6».
 *
 * Слова для двух ближайших дней даёт `Intl.RelativeTimeFormat` с
 * `numeric: 'auto'` — именно он превращает «-1 день» в «вчера».
 */
export function humanDay(key: string, locale = 'ru'): string {
  const relative = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });

  if (key === dayKey()) {
    const today = relative.format(0, 'day');
    return today.charAt(0).toUpperCase() + today.slice(1);
  }
  if (key === dayKey(Date.now() - MS_DAY)) {
    const yesterday = relative.format(-1, 'day');
    return yesterday.charAt(0).toUpperCase() + yesterday.slice(1);
  }

  const d = keyToDate(key);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return fmt(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}

/** Полная дата с днём недели, для подсказок на карте активности. */
export function humanDayFull(key: string, locale = 'ru'): string {
  return fmt(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(keyToDate(key));
}

/* -------------------------------------------------------------------------
   Статистика
   ------------------------------------------------------------------------- */

export function entrySeconds(e: FlowEntry): number {
  return (e.end - e.start) / 1000;
}

export function focusSecondsOfDay(entries: FlowEntry[], key: string): number {
  return entries
    .filter((e) => e.mode === 'focus' && dayKey(e.start) === key)
    .reduce((s, e) => s + entrySeconds(e), 0);
}

/**
 * Сколько дней подряд человек работал, считая от сегодня.
 *
 * Вчерашний день тоже считается началом: если сегодня работа ещё не
 * начата, обнулять счётчик в полночь было бы несправедливо.
 */
export function streak(entries: FlowEntry[]): number {
  const days = new Set(entries.filter((e) => e.mode === 'focus').map((e) => dayKey(e.start)));
  if (days.size === 0) return 0;

  let cursor = startOfDay();
  if (!days.has(dayKey(cursor))) {
    cursor = addDays(cursor, -1);
    if (!days.has(dayKey(cursor))) return 0;
  }

  let count = 0;
  while (days.has(dayKey(cursor))) {
    count++;
    cursor = addDays(cursor, -1);
  }
  return count;
}

export function bestStreak(entries: FlowEntry[]): number {
  const days = [...new Set(entries.filter((e) => e.mode === 'focus').map((e) => dayKey(e.start)))]
    .sort()
    .map((k) => keyToDate(k).getTime());

  let best = 0;
  let run = 0;
  let prev: number | null = null;
  for (const t of days) {
    run = prev !== null && t - prev === MS_DAY ? run + 1 : 1;
    best = Math.max(best, run);
    prev = t;
  }
  return best;
}

/* -------------------------------------------------------------------------
   Экспорт
   ------------------------------------------------------------------------- */

/** Экранирование ячейки по RFC 4180. */
function csvCell(v: string): string {
  return /[",\n;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function buildCsv(
  entries: FlowEntry[],
  taskTitle: (id: string | null) => string,
  projectName: (id: string | null) => string,
): string {
  const head = [
    'date',
    'start',
    'end',
    'duration',
    'hours',
    'mode',
    'task',
    'project',
    'interruptions',
    'completed',
    'note',
  ];

  const rows = entries.map((e) => {
    const dur = entrySeconds(e);
    return [
      dayKey(e.start),
      new Date(e.start).toISOString(),
      new Date(e.end).toISOString(),
      fmtClock(dur),
      String(Math.round((dur / 3600) * 100) / 100),
      e.mode,
      taskTitle(e.taskId),
      projectName(e.projectId),
      String(e.interruptions),
      e.completed ? 'yes' : 'no',
      e.note,
    ]
      .map(csvCell)
      .join(',');
  });

  // BOM: без него Excel открывает кириллицу как мусор.
  return `\uFEFF${[head.join(','), ...rows].join('\n')}`;
}

export function downloadFile(name: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}
